import React from 'react';
import { Slide } from '../../components/deck/Slide';
import { Caption, Eyebrow, INK, Label, Reveal, Title } from '../../components/deck/Type';
const SERIF = {
  fontFamily: 'var(--font-serif)'
} as const;
type Level = 1 | 2 | 3;
const capabilities = ['Reasoning', 'Curated data', 'Workflow & industry assets', 'Institutional intelligence'];
const categories: {
  name: string;
  note: string;
  levels: Level[];
}[] = [{
  name: 'General LLMs',
  note: 'Strong reasoning, weak institutional specialization',
  levels: [3, 1, 1, 1]
}, {
  name: 'Data providers',
  note: 'Strong external data, limited reasoning and workflow',
  levels: [1, 3, 1, 1]
}, {
  name: 'Workflow software',
  note: 'Strong process, limited intelligence',
  levels: [1, 2, 3, 1]
}];
const dgpt = ['Orchestrates frontier and specialized models', 'Routes across paid, partner, public and client data', 'Industry-native templates, scoring and data rooms', 'Persistent firm memory and outcome feedback'];
const levelText: Record<Level, string> = {
  1: 'Limited',
  2: 'Partial',
  3: 'Core'
};
function LevelBar({
  level
}: {
  level: Level;
}) {
  return <div className="flex items-center gap-[0.8cqw]" data-id="element-145" data-mp="26bgooxg1um0t">
      <div className="flex gap-[0.25cqw]" aria-hidden data-id="element-146" data-mp="26crnsjyjv5ll">
        {[1, 2, 3].map(n => <span key={n} className="h-[0.9cqh] w-[1.8cqw]" style={{
        background: n <= level ? '#AEB7C7' : 'rgba(255,255,255,0.1)'
      }} data-id="element-147" data-mp="2k83n74nnhk8u" />)}
      </div>
      <span className="text-[0.82cqw] text-[#7C8699]" data-id="element-148" data-mp="2u7cchk7kmaov">{levelText[level]}</span>
    </div>;
}
export function ArchitecturePositioningSlide() {
  return <Slide data-id="element-149" data-mp="33y7aflu6iyq5">
      <div className="flex items-start justify-between gap-[3cqw]" data-id="element-150" data-mp="33xxhbpdu8bg8">
        <div className="max-w-[62%]" data-id="element-151" data-mp="3409wjus7o7pl">
          <Eyebrow data-id="element-152" data-mp="2yykmahctby2k">Positioning</Eyebrow>
          <Title className="mt-[2.2cqh]" data-id="element-153" data-mp="2yzm7vfrgg2yj">
            The differentiation is the architecture — not simply another model.
          </Title>
        </div>
        <Caption className="max-w-[28%] text-right" data-id="element-154" data-mp="38026obx96lx2">
          Architectural differentiation, not a product ranking. We integrate
          foundation models and data providers rather than replace them.
        </Caption>
      </div>

      <div className="mt-auto grid grid-cols-[15cqw_1fr_1fr_1fr_1.25fr]" data-id="element-155" data-mp="2am8wi93ol623">
        <span data-id="element-156" data-mp="2emj57tiuyakn" />
        {categories.map(c => <div key={c.name} className="border-b border-white/15 px-[1.2cqw] pb-[2cqh]" data-id="element-157" data-mp="2eky75txc49i1">
            <p className="text-[1.5cqw] leading-none text-[#C3CBD9]" style={SERIF} data-id="element-158" data-mp="2jf2fagic6nkp">
              {c.name}
            </p>
            <p className="mt-[1.2cqh] text-[0.86cqw] leading-snug text-[#7C8699]" data-id="element-159" data-mp="2of89a3c2wm5y">{c.note}</p>
          </div>)}
        <div className="border-b border-[#3B8CFF]/50 bg-[#3B8CFF]/[0.08] px-[1.4cqw] pb-[2cqh] pt-[2cqh]" data-id="element-160" data-mp="2ybbczdmkwsv0">
          <p className="text-[1.5cqw] leading-none text-[#F4F7FC]" style={SERIF} data-id="element-161" data-mp="32d58l7hx36ob">
            DiligenceGPT™
          </p>
          <p className="mt-[1.2cqh] text-[0.86cqw] leading-snug text-[#AEC9F5]" data-id="element-162" data-mp="13y2ai5sga7m9">
            Data · Reasoning · Workflow · Firm memory · Industry assets · Deterministic functions
          </p>
        </div>

        {capabilities.map((cap, row) => <React.Fragment key={cap} data-id="element-163" data-mp="10gxpowvjcfcz" data-mpi={row}>
            <Reveal delay={0.05 * row} className="flex items-center border-b border-white/8 py-[2.6cqh] pr-[1cqw]" data-id="element-164" data-mp="vo4qspmr018t">
              <Label tone="#AEB7C7" data-id="element-165" data-mp="qq36609obtv8">{cap}</Label>
            </Reveal>
            {categories.map(c => <div key={c.name} className="flex items-center border-b border-white/8 px-[1.2cqw] py-[2.6cqh]" data-id="element-166" data-mp="mp9jfa7ywl3x">
                <LevelBar level={c.levels[row]} data-id="element-167" data-mp="1jd5y29juh6ci" />
              </div>)}
            <div className="flex items-center border-b border-[#3B8CFF]/20 bg-[#3B8CFF]/[0.08] px-[1.4cqw] py-[2.6cqh]" data-id="element-168" data-mp="1efdyqugvehya">
              <span className="text-[0.95cqw] leading-snug text-[#E3E8F1]" data-id="element-169" data-mp="1afdir5e4hyp4">{dgpt[row]}</span>
            </div>
          </React.Fragment>)}
      </div>

      <p className="mt-[2.4cqh] text-[0.8cqw]" style={{
      color: INK.grey
    }} data-id="element-170" data-mp="3v4jdpw5m8m15">
        Category assessments describe typical architectural focus, not specific vendors.
      </p>
    </Slide>;
}